
import React, { useRef } from 'react';
import { Button } from "@/components/ui/button";
import * as XLSX from 'xlsx';
import { processExcelData, validateTimeFormat } from './excel';
import { useToast } from '@/hooks/use-toast';
import { Upload } from 'lucide-react';
import { ExcelRowData } from './excel/types';

interface ExcelUploadProps {
  onDataLoaded: (data: any[]) => void;
}

const ExcelUpload: React.FC<ExcelUploadProps> = ({ onDataLoaded }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    console.log('File selected:', file?.name);

    if (!file) {
      console.log('No file selected');
      return;
    }

    try {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        console.log('File read successfully');
        const data = e.target?.result as ArrayBuffer;
        processData(data);
      };

      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        toast({
          title: "Error",
          description: "Failed to read file",
          variant: "destructive",
        });
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error processing file:', error);
      toast({
        title: "Error",
        description: "Failed to process file",
        variant: "destructive",
      });
    }
  };

  const processData = (data: ArrayBuffer) => {
    try {
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      const options = { 
        raw: false,
        dateNF: 'dd/mm/yyyy', // Preserve exact date formats from Excel
        cellDates: false,      // Don't automatically convert to Date objects
        cellText: true,        // Keep as text
        raw_dates: false       // Don't interpret as raw dates
      };
      
      const jsonData = XLSX.utils.sheet_to_json<ExcelRowData>(worksheet, options);
      console.log('Excel data converted to JSON:', jsonData);
      
      // Log data structure samples
      if (jsonData.length > 0) {
        console.log('First row data:', jsonData[0]);
        console.log('Date field sample:', jsonData[0].Date);
        console.log('Date field type:', typeof jsonData[0].Date);
      }

      const processedData = processExcelData(jsonData);
      console.log('Data processed:', processedData);

      // Validate the time format
      const validatedData = validateTimeFormat(processedData);
      console.log('Data validated:', validatedData);

      onDataLoaded(validatedData);
      toast({
        title: "Success",
        description: "Data loaded successfully",
      });
    } catch (error) {
      console.error('Error processing file:', error);
      toast({
        title: "Error",
        description: "Failed to process file. Please check the file format.",
        variant: "destructive",
      });
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,.csv,.xlsx,.xls"
        style={{ display: 'none' }}
      />
      <Button
        onClick={handleClick}
        variant="default"
        className="bg-slate-700 hover:bg-slate-600"
      >
        <Upload className="mr-2 h-4 w-4" />
        Upload File
      </Button>
    </div>
  );
};

export default ExcelUpload;
